import { useCallback, useEffect, useEffectEvent, useRef, useState, type DetailedHTMLProps, type ImgHTMLAttributes } from 'react';
import ExifWorker from 'workers/exif-worker?worker&inline';
import type { Tags } from 'exifreader';
const exifWorker = new ExifWorker();

type ExifSuccess = {
	success: true;
	data: Tags;
	src: string;
}
type ExifFailure = {
	success: false;
	error: string;
	src: string;
}
type ExifMessage = ExifSuccess | ExifFailure;

type Props = DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement> & {
	fallback?: string;
	fallbackAlt?: string;
};

const logErrors = localStorage.getItem('LOG_IMAGE_ERRORS');

const Image = (props: Props) => {
	const { fallbackAlt, ...rest } = props;
	const [src, setSrc] = useState(props.src);
	const [alt, setAlt] = useState(props.alt);
	const exifLoadingRef = useRef(false);

	const handleExif = useEffectEvent((e: MessageEvent<ExifMessage>) => {
		if (e.data.src !== props.src) return;

		if (e.data.success) {
			// load the image's exif data description THEN about THEN title in order of precedence 
			setAlt(e.data.data.description?.description ?? e.data.data.about?.description ?? e.data.data.title?.description ?? fallbackAlt);
		} else { 
			if (logErrors) { 
				console.error(e.data.error);
			}
			setAlt(fallbackAlt);
		}

		exifLoadingRef.current = false;
	});

	useEffect(() => {
		if (alt || !props.src || props.src.endsWith('.svg')) return;

		exifWorker.addEventListener('message', handleExif);
		
		if (!exifLoadingRef.current) { 
			exifLoadingRef.current = true;
			fetch(props.src, {
				mode: 'cors',
				headers: {
					origin: window.location.origin
				}
			}).then(res => {
				if (res.ok) {
					return res.arrayBuffer();
				} else { 
					if (logErrors) console.error(res.headers, res.status);
				}
			}).then(buffer => {
				if (buffer) {
					exifWorker.postMessage({ src: props.src, buffer }, [buffer]);
				} else { 
					exifLoadingRef.current = false;
				}
			}).catch(e => { 
				if (logErrors) { 
					console.error(e);
				}
				exifLoadingRef.current = false;
			});
		}
		return () => {
			exifWorker.removeEventListener('message', handleExif);
		};
	}, [props.src, alt]);

  const onError = useCallback(() => {
    setSrc(props.fallback);
  }, [props.fallback]);
	return <img {...rest} alt={ alt} src={src} onError={onError} />;
};

export default Image;
